/* Compras (Purchase Module) — Phase 9.
 * Lista + Modal "Nova Compra" com 3 metodos de entrada:
 *   1) Manual
 *   2) Upload XML
 *   3) Da Sistema Fiscal (manifesto)
 * Conciliacao de itens (vincular ou auto-cadastrar) + pagamentos + finalize.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../lib/api';
import {
  Plus, X, Save, Trash2, Search, Upload, FileText, ShoppingCart, CheckCircle, AlertCircle, Link2, Sparkles, Zap, Banknote, Send, Edit2, Check, Calendar, MoreVertical, Truck, ArrowRightLeft, AlertTriangle, Printer, PackagePlus, UserPlus, RefreshCw, KeyRound,
} from 'lucide-react';
import { ManifestoFetchChaveModal, runManifestoSync } from '../components/ManifestoFetchChave';
import { localDateISO } from '../lib/dateUtils';
import { confirmDialog } from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const ERP = `${API}/erp`;

// iter156ev: modal de cadastro rapido de produto (usado pela tela de Compras)
const QuickProductModal = ({ initial, companyId, onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', sku: '', ean: '', ncm: '', unit: 'UN', cost_price: 0, price: 0, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error('Informe o nome do item'); return; }
    setSaving(true);
    try {
      const ncm = (f.ncm || '').replace(/\D/g, '');
      const payload = {
        name: f.name.trim(),
        sku: (f.sku || '').trim() || `ITEM-${Date.now().toString().slice(-8)}`,
        barcode: (f.ean || '').trim() || null,
        price: Number(f.price) || 0,
        cost_price: Number(f.cost_price) || 0,
        fiscal: { unit_commercial: (f.unit || 'UN').toUpperCase(), ...(ncm ? { ncm } : {}) },
        ...(companyId ? { company_id: companyId } : {}),
      };
      const { data } = await axios.post(`${API}/products`, payload, { withCredentials: true });
      onSaved({ id: data.id, name: data.name, sku: data.sku, ean: data.barcode, price: data.price, cost_price: data.cost_price });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao cadastrar item'); }
    finally { setSaving(false); }
  };
  const lbl = "text-[10px] font-bold text-slate-500 uppercase block mb-1";
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="quick-product-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-black text-slate-900 flex items-center gap-2"><PackagePlus size={18} className="text-violet-600" /> Cadastro rapido de item</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={lbl}>Nome *</label><input value={f.name} onChange={e => upd('name', e.target.value)} autoFocus className={inputClass} data-testid="quick-product-name" /></div>
          <div><label className={lbl}>Codigo / SKU</label><input value={f.sku} onChange={e => upd('sku', e.target.value)} placeholder="gera automatico se vazio" className={inputClass} data-testid="quick-product-sku" /></div>
          <div><label className={lbl}>EAN</label><input value={f.ean} onChange={e => upd('ean', e.target.value)} className={inputClass} data-testid="quick-product-ean" /></div>
          <div><label className={lbl}>NCM</label><input value={f.ncm} onChange={e => upd('ncm', e.target.value)} className={inputClass} data-testid="quick-product-ncm" /></div>
          <div><label className={lbl}>Unidade</label><input value={f.unit} onChange={e => upd('unit', e.target.value.toUpperCase())} className={inputClass} data-testid="quick-product-unit" /></div>
          <div><label className={lbl}>Custo (R$)</label><input type="number" step="0.01" value={f.cost_price} onChange={e => upd('cost_price', e.target.value)} onFocus={e => e.target.select()} className={inputClass} data-testid="quick-product-cost" /></div>
          <div><label className={lbl}>Preco de venda (R$)</label><input type="number" step="0.01" value={f.price} onChange={e => upd('price', e.target.value)} onFocus={e => e.target.select()} className={inputClass} data-testid="quick-product-price" /></div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900">Cancelar</button>
          <button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2" data-testid="quick-product-save"><Save size={14} /> {saving ? 'Salvando...' : 'Salvar e preencher linha'}</button>
        </div>
      </div>
    </div>, document.body);
};
// iter156ew: modal de cadastro rapido de fornecedor (usado pela tela de Compras)
const QuickSupplierModal = ({ initial, onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', document: '', ie: '', phone: '', email: '', ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error('Informe o nome do fornecedor'); return; }
    setSaving(true);
    try {
      const doc = (f.document || '').replace(/\D/g, '');
      const payload = { name: f.name.trim(), doc_type: doc.length > 11 ? 'cnpj' : 'cpf', document: doc, phone: f.phone || '', email: f.email || '', transp_ie: (f.ie || '').trim() };
      const { data } = await axios.post(`${ERP}/suppliers`, payload, { withCredentials: true });
      onSaved({ id: data.id, name: data.name || payload.name, document: data.document || doc, transp_ie: payload.transp_ie });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao cadastrar fornecedor'); }
    finally { setSaving(false); }
  };
  const lbl = "text-[10px] font-bold text-slate-500 uppercase block mb-1";
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()} data-testid="quick-supplier-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-black text-slate-900 flex items-center gap-2"><UserPlus size={18} className="text-violet-600" /> Cadastro rapido de fornecedor</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className={lbl}>Nome / Razao social *</label><input value={f.name} onChange={e => upd('name', e.target.value)} autoFocus className={inputClass} data-testid="quick-supplier-name" /></div>
          <div><label className={lbl}>CNPJ / CPF</label><input value={f.document} onChange={e => upd('document', e.target.value)} className={inputClass} data-testid="quick-supplier-document" /></div>
          <div><label className={lbl}>IE</label><input value={f.ie} onChange={e => upd('ie', e.target.value)} className={inputClass} data-testid="quick-supplier-ie" /></div>
          <div><label className={lbl}>Telefone</label><input value={f.phone} onChange={e => upd('phone', e.target.value)} className={inputClass} data-testid="quick-supplier-phone" /></div>
          <div><label className={lbl}>E-mail</label><input value={f.email} onChange={e => upd('email', e.target.value)} className={inputClass} data-testid="quick-supplier-email" /></div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-slate-900">Cancelar</button>
          <button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-bold disabled:opacity-50 flex items-center gap-2" data-testid="quick-supplier-save"><Save size={14} /> {saving ? 'Salvando...' : 'Salvar e preencher'}</button>
        </div>
      </div>
    </div>, document.body);
};

const inputClass = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:border-violet-600 focus:ring-2 focus:ring-violet-500/20";
const labelCls = "block text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1";

// iter156dt9: usa toLocaleString pt-BR para separador de milhar (1234.5 -> "1.234,50")
const fmtBRL = (n) => `R$ ${(Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// iter156f: input BRL sem spinner (type=text) com formatacao automatica em virgula
// (pt-BR): user digita "12345" -> mostra "123,45". Aceita input com vírgula ou ponto.
const parseBRLNumber = (s) => {
  if (s === '' || s == null) return null;
  const str = String(s).replace(/[^\d,.-]/g, '');
  // se tem virgula, ela e o decimal; ponto e separador de milhar
  if (str.includes(',')) {
    const clean = str.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(clean);
    return isNaN(n) ? null : n;
  }
  const n = parseFloat(str);
  return isNaN(n) ? null : n;
};
const fmtBRLNumber = (n) => {
  if (n == null || n === '' || isNaN(Number(n))) return '';
  return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * BRLInput: input controlado que exibe valores em formato pt-BR (12.345,67)
 * sem setinhas de spinner. onChange retorna o valor numerico raw (ou null).
 * Props adicionais: prefix (ex: "R$"), placeholder, className, disabled, testid.
 */
const BRLInput = ({ value, onChange, prefix, placeholder, className = '', disabled, testid, allowEmpty = false, ariaLabel }) => {
  const [local, setLocal] = React.useState(() => value == null || value === '' ? '' : fmtBRLNumber(value));
  const [focused, setFocused] = React.useState(false);
  // Sync from external value when nao esta editando
  React.useEffect(() => {
    if (!focused) setLocal(value == null || value === '' ? '' : fmtBRLNumber(value));
  }, [value, focused]);
  return (
    <div className="relative w-full">
      {prefix && <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 pointer-events-none">{prefix}</span>}
      <input
        type="text"
        inputMode="decimal"
        value={local}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          const parsed = parseBRLNumber(local);
          if (parsed === null) {
            if (allowEmpty) { onChange(null); setLocal(''); }
            else { onChange(0); setLocal(fmtBRLNumber(0)); }
          } else {
            onChange(parsed);
            setLocal(fmtBRLNumber(parsed));
          }
        }}
        onChange={e => {
          const raw = e.target.value;
          setLocal(raw);
          // atualiza estado externo em tempo real para calculos de total
          const p = parseBRLNumber(raw);
          if (p === null && allowEmpty) onChange(null);
          else if (p !== null) onChange(p);
        }}
        placeholder={placeholder}
        className={`${className} ${prefix ? 'pl-6' : ''}`}
        disabled={disabled}
        data-testid={testid}
        aria-label={ariaLabel}
      />
    </div>
  );
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return iso.slice(0, 10); }
};

const STATUS_META = {
  draft: { label: 'Rascunho', color: 'bg-slate-100 text-slate-700' },
  finalized: { label: 'Finalizada', color: 'bg-emerald-100 text-emerald-700' },
};

export const PurchasesPage = ({ companyId }) => {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role || '') === 'superadmin';
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [methodPicker, setMethodPicker] = useState(false);     // show 3-button picker
  const [draft, setDraft] = useState(null);                     // current draft data
  const [deleteTarget, setDeleteTarget] = useState(null);       // iter77ct.108: compra selecionada p/ exclusao (superadmin modal)

  const load = useCallback(async () => {
    if (!companyId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: companyId });
      if (filterStatus) params.set('status', filterStatus);
      if (search) params.set('q', search);
      const { data } = await axios.get(`${ERP}/purchases?${params}`, { withCredentials: true });
      setRows(data.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao carregar compras');
    } finally { setLoading(false); }
  }, [companyId, filterStatus, search]);

  useEffect(() => { load(); }, [load]);

  const startManual = () => {
    setDraft({
      company_id: companyId, source: 'manual',
      supplier_name: '', supplier_cnpj: '', items: [], payments: [],
    });
    setMethodPicker(false);
  };

  const startXml = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const tid = toast.loading('Lendo XML...');
    try {
      const { data } = await axios.post(`${ERP}/purchases/parse-xml`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.dismiss(tid);
      toast.success(`XML lido: ${data.items?.length || 0} itens, fornecedor ${data.supplier_name}`);
      setDraft({ ...data, company_id: companyId, payments: [] });
      setMethodPicker(false);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao ler XML');
    }
  };

  // iter156fh: modal moderno (substitui o window.prompt) — so notas emitidas contra o CNPJ da filial selecionada
  const [manifestoPicker, setManifestoPicker] = useState(false);
  const startFromManifesto = () => setManifestoPicker(true);
  const importManifestoDoc = async (doc) => {
    const tid = toast.loading('Carregando nota do manifesto...');
    try {
      const { data: parsed } = await axios.post(`${ERP}/purchases/from-manifesto/${doc.id}`, {}, { withCredentials: true });
      toast.dismiss(tid);
      setDraft({ ...parsed, company_id: companyId, payments: [] });
      setManifestoPicker(false);
      setMethodPicker(false);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao importar do manifesto');
    }
  };
  // Deep link vindo da tela NF de Entrada: /purchases?manifesto_doc=<id>
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const mid = sp.get('manifesto_doc');
    const openId = sp.get('open');
    if (mid && companyId) {
      importManifestoDoc({ id: mid });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (openId && companyId) {
      axios.get(`${ERP}/purchases?company_id=${companyId}&limit=500`, { withCredentials: true })
        .then(({ data }) => { const row = (data.data || []).find(x => x.id === openId); if (row) setDraft({ ...row, _editing: true }); else toast.error('Compra nao encontrada nesta filial'); })
        .catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // iter77ct.108: Excluir uma Compra (draft ou finalizada).
  //  - Admin comum: confirmDialog simples + cascata automatica (reverte estoque + apaga financeiro).
  //  - SuperAdmin: abre modal detalhado com aviso completo dos impactos.
  const askDelete = (row) => {
    if (isSuperAdmin) {
      setDeleteTarget(row);
      return;
    }
    (async () => {
      const isFinalized = row.status === 'received' || row.status === 'finalized';
      const nfLabel = row.nf_numero ? `NF ${row.nf_numero}` : `Compra #${row.id.slice(-6)}`;
      const msg = isFinalized
        ? `Excluir ${nfLabel}?\n\nIsso vai:\n• Reverter o estoque desta filial (${(row.items || []).length} item(ns))\n• Apagar as contas a pagar geradas pela compra`
        : `Excluir rascunho da ${nfLabel}?`;
      const ok = await confirmDialog({ title: msg, confirmLabel: 'Excluir', danger: true });
      if (!ok) return;
      await doDelete(row.id, true);
    })();
  };

  const doDelete = async (id, cascade) => {
    const tid = toast.loading('Excluindo compra...');
    try {
      const { data } = await axios.delete(`${ERP}/purchases/${id}?cascade=${cascade}`, { withCredentials: true });
      toast.dismiss(tid);
      const parts = ['Compra excluida'];
      if (data.reverted_stock_items > 0) parts.push(`${data.reverted_stock_items} item(ns) revertido(s) no estoque`);
      if (data.deleted_financials > 0) parts.push(`${data.deleted_financials} conta(s) a pagar apagada(s)`);
      toast.success(parts.join(' · '));
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  if (!companyId) {
    return <div className="bg-amber-50 border border-amber-200 text-amber-800 p-6 rounded-2xl text-center">Selecione uma empresa para gerenciar Compras.</div>;
  }

  return (
    <div className="space-y-4" data-testid="purchases-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2"><ShoppingCart className="text-violet-600" /> Compras</h2>
          <p className="text-xs text-slate-500">Entradas de NF de fornecedores. 3 metodos: Manual, XML, Manifesto.</p>
        </div>
        <button onClick={() => setMethodPicker(true)} className="px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-bold text-sm shadow-md shadow-violet-500/30 flex items-center gap-2" data-testid="purchase-new-btn">
          <Plus size={16} /> Nova Compra
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 shadow-sm">
        <div className="relative md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por fornecedor, CNPJ, numero NF..." className={`${inputClass} pl-9`} data-testid="purchase-search" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={inputClass} data-testid="purchase-filter-status">
          <option value="">Todos os status</option>
          <option value="draft">Rascunho</option>
          <option value="finalized">Finalizada</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" /></div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center" data-testid="purchases-empty">
            <ShoppingCart size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Nenhuma compra ainda. Comece em &quot;Nova Compra&quot;.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-bold text-xs uppercase">Fornecedor</th>
                <th className="text-left px-3 py-2 font-bold text-xs uppercase">NF / Origem</th>
                <th className="text-left px-3 py-2 font-bold text-xs uppercase">Itens</th>
                <th className="text-right px-3 py-2 font-bold text-xs uppercase">Total</th>
                <th className="text-center px-3 py-2 font-bold text-xs uppercase">Status</th>
                <th className="text-left px-3 py-2 font-bold text-xs uppercase" title="Data em que a NF foi emitida pelo fornecedor">Data Emissao</th>
                <th className="text-left px-3 py-2 font-bold text-xs uppercase" title="Data em que a NF foi lancada no sistema">Data Lancamento</th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map(p => {
                const sm = STATUS_META[p.status] || STATUS_META.draft;
                return (
                  <tr key={p.id} className="hover:bg-violet-50/40 cursor-pointer" onClick={() => setDraft({ ...p, _editing: true })} data-testid={`purchase-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <p className="font-bold text-slate-800 text-xs">{p.supplier_name || '—'}</p>
                      <p className="text-[10px] text-slate-400">{p.supplier_cnpj}</p>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <p>NF {p.nf_numero || '—'} {p.nf_serie ? `s${p.nf_serie}` : ''}</p>
                      <p className="text-[10px] text-slate-400 uppercase">{p.source}</p>
                    </td>
                    <td className="px-3 py-2 text-xs">{(p.items || []).length}</td>
                    <td className="px-3 py-2 text-right font-black text-slate-800">{fmtBRL(p.total)}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${sm.color}`}>{sm.label}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500" data-testid={`purchase-emit-date-${p.id}`}>
                      {p.nf_emit_date ? fmtDate(p.nf_emit_date) : (p.source === 'manual' ? '—' : fmtDate(p.created_at))}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500" data-testid={`purchase-imported-at-${p.id}`}>
                      {fmtDate(p.imported_at || p.created_at)}
                    </td>
                    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => askDelete(p)}
                        className="p-1.5 rounded hover:bg-rose-100 text-rose-600 transition"
                        title="Excluir compra"
                        data-testid={`purchase-delete-${p.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {methodPicker && (
        <MethodPickerModal onClose={() => setMethodPicker(false)} onManual={startManual} onXml={startXml} onManifesto={startFromManifesto} />
      )}
      {manifestoPicker && (
        <ManifestoPickerModal companyId={companyId} onClose={() => setManifestoPicker(false)} onPick={importManifestoDoc} />
      )}
      {draft && (
        <PurchaseForm draft={draft} companyId={companyId} onClose={() => setDraft(null)} onSaved={() => { setDraft(null); load(); }} />
      )}
      {deleteTarget && (
        <SuperAdminDeletePurchaseModal
          purchase={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={(cascade) => doDelete(deleteTarget.id, cascade)}
        />
      )}
    </div>
  );
};


/* ========================= MethodPickerModal ========================= */
// iter156fh: seletor moderno de DF-e do Manifesto (filtrado pelo CNPJ da filial)
const ManifestoPickerModal = ({ companyId, onClose, onPick }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [onlyFree, setOnlyFree] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [fetchChaveOpen, setFetchChaveOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const handleSync = async () => {
    setSyncing(true);
    try { await runManifestoSync(companyId); setReloadKey(k => k + 1); }
    finally { setSyncing(false); }
  };
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: '500' });
        if (companyId) params.set('company_id', companyId);
        if (status) params.set('status', status);
        const { data } = await axios.get(`${ERP}/manifesto?${params}`, { withCredentials: true });
        setDocs(data.data || []);
      } catch { setDocs([]); }
      finally { setLoading(false); }
    })();
  }, [companyId, status, reloadKey]);
  const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '-';
  const rows = docs.filter(d => {
    if (onlyFree && (d.linked_purchase || d.imported_invoice_id)) return false;
    if (q) { const t = q.toLowerCase(); if (!(`${d.emitente_nome} ${d.emitente_cnpj} ${d.chave} ${d.valor_nf}`.toLowerCase().includes(t))) return false; }
    return true;
  });
  const STATUS = { pendente: 'Pendente', ciencia: 'Ciencia', confirmada: 'Confirmada', desconhecida: 'Desconhecida', nao_realizada: 'Nao realizada' };
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="manifesto-picker-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-black text-slate-900 flex items-center gap-2"><Zap size={18} className="text-sky-600" /> Puxar do Sistema Fiscal</h3>
            <p className="text-xs text-slate-500">Notas emitidas contra o CNPJ desta filial (sincronizadas em Fiscal › NF de Entrada). Ao importar, a compra alimenta <b>fiscal + estoque + financeiro</b>.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSync} disabled={syncing} className="px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:bg-slate-300 text-white text-xs font-bold flex items-center gap-1.5" data-testid="manifesto-picker-sync" title="Consultar a SEFAZ agora (notas emitidas contra o CNPJ desta filial)">
              {syncing ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <RefreshCw size={14} />} Sincronizar SEFAZ
            </button>
            <button onClick={() => setFetchChaveOpen(true)} className="px-3 py-2 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 text-xs font-bold flex items-center gap-1.5" data-testid="manifesto-picker-fetch-chave" title="Buscar uma nota especifica pela chave de acesso">
              <KeyRound size={14} /> Buscar pela chave
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="manifesto-picker-close"><X size={20} /></button>
          </div>
        </div>
        <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b border-slate-100">
          <div className="relative flex-1 min-w-[220px]"><Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Fornecedor, CNPJ, chave ou valor..." className={inputClass + ' pl-8'} data-testid="manifesto-picker-search" /></div>
          <select value={status} onChange={e => setStatus(e.target.value)} className={inputClass + ' w-auto'} data-testid="manifesto-picker-status">
            <option value="">Todos os status</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer"><input type="checkbox" checked={onlyFree} onChange={e => setOnlyFree(e.target.checked)} className="accent-sky-600" /> Ocultar ja importadas</label>
          <span className="text-xs text-slate-400">{rows.length} nota(s)</span>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="p-10 text-center text-slate-400 text-sm">Carregando...</div> : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">Nenhuma nota disponivel. Clique em <b>Sincronizar SEFAZ</b> para consultar as notas emitidas contra o CNPJ desta filial, ou use <b>Buscar pela chave</b> se tiver o DANFE em maos.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0"><tr><th className="text-left px-4 py-2">Emitente</th><th className="text-left px-3 py-2">Emissao</th><th className="text-right px-3 py-2">Valor</th><th className="text-center px-3 py-2">Manifesto</th><th className="px-3 py-2"></th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map(d => (
                  <tr key={d.id} className="hover:bg-sky-50/50" data-testid={`manifesto-picker-row-${d.id}`}>
                    <td className="px-4 py-2.5"><p className="font-bold text-slate-800">{d.emitente_nome}</p><p className="text-[10px] text-slate-400 font-mono">CNPJ {d.emitente_cnpj} · {(d.chave || '').slice(-12)}</p></td>
                    <td className="px-3 py-2.5 text-slate-600">{fmtD(d.dhEmi)}</td>
                    <td className="px-3 py-2.5 text-right font-black text-slate-800">{fmtBRL(d.valor_nf || 0)}</td>
                    <td className="px-3 py-2.5 text-center"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{STATUS[d.manifesto_status] || d.manifesto_status}</span>{d.linked_purchase && <span className="block text-[9px] text-emerald-600 font-bold">Importada</span>}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button onClick={() => onPick(d)} disabled={!!d.linked_purchase || !!d.awaiting_full_xml} title={d.awaiting_full_xml ? 'A SEFAZ ainda nao liberou o XML desta nota — sincronize novamente mais tarde' : ''} className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-[11px] font-bold disabled:opacity-40" data-testid={`manifesto-picker-import-${d.id}`}>{d.linked_purchase ? 'Ja importada' : d.awaiting_full_xml ? 'Aguardando XML' : 'Importar para compra'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {fetchChaveOpen && <ManifestoFetchChaveModal companyId={companyId} onClose={() => setFetchChaveOpen(false)} onDone={() => setReloadKey(k => k + 1)} />}
    </div>, document.body);
};

const MethodPickerModal = ({ onClose, onManual, onXml, onManifesto }) => {
  const fileRef = React.useRef();
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="method-picker-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-violet-600 to-cyan-500 px-5 py-4 text-white flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Nova Compra</p>
            <p className="text-xl font-black">Como deseja iniciar?</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg"><X /></button>
        </div>
        <div className="p-5 space-y-3">
          <button onClick={onManual} className="w-full flex items-center gap-3 p-4 border border-slate-200 hover:border-violet-400 hover:bg-violet-50 rounded-xl text-left transition-colors" data-testid="method-manual">
            <Sparkles className="text-violet-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-800 text-sm">Entrada Manual</p>
              <p className="text-[11px] text-slate-500">Cadastro tradicional digitando os dados do fornecedor e itens.</p>
            </div>
          </button>
          <button onClick={() => fileRef.current?.click()} className="w-full flex items-center gap-3 p-4 border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-xl text-left transition-colors" data-testid="method-xml">
            <Upload className="text-emerald-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-800 text-sm">Importar XML da NF-e</p>
              <p className="text-[11px] text-slate-500">Faca upload do arquivo .xml — o sistema le fornecedor e itens automaticamente.</p>
            </div>
          </button>
          <input ref={fileRef} type="file" accept=".xml,application/xml,text/xml" className="hidden" onChange={(e) => e.target.files?.[0] && onXml(e.target.files[0])} data-testid="method-xml-input" />
          <button onClick={onManifesto} className="w-full flex items-center gap-3 p-4 border border-slate-200 hover:border-sky-400 hover:bg-sky-50 rounded-xl text-left transition-colors" data-testid="method-manifesto">
            <Zap className="text-sky-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-slate-800 text-sm">Puxar do Sistema Fiscal</p>
              <p className="text-[11px] text-slate-500">Selecione uma nota ja identificada no Manifesto contra o seu CNPJ.</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};


/* ========================= PurchaseForm ========================= */
const PurchaseForm = ({ draft, companyId, onClose, onSaved }) => {
  const isEdit = !!draft?.id;
  const isFinalized = draft?.status === 'finalized';
  const [supplierName, setSupplierName] = useState(draft.supplier_name || '');
  const [supplierCnpj, setSupplierCnpj] = useState(draft.supplier_cnpj || '');
  const [supplierIE, setSupplierIE] = useState(draft.supplier_ie || '');
  const [nfNum, setNfNum] = useState(draft.nf_numero || '');
  const [nfSerie, setNfSerie] = useState(draft.nf_serie || '');
  const [items, setItems] = useState(draft.items || []);
  const [discount, setDiscount] = useState(draft.discount || 0);
  const [freight, setFreight] = useState(draft.freight || 0);
  const [otherCharges, setOtherCharges] = useState(draft.other_charges || 0);
  const [payments, setPayments] = useState(draft.payments || []);
  const [notes, setNotes] = useState(draft.notes || '');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [products, setProducts] = useState([]);                  // pool for linking
  const [searchProd, setSearchProd] = useState('');
  const [searchTargetIdx, setSearchTargetIdx] = useState(null);  // which row is searching
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('itens');                      // itens | pagamentos
  // iter156es: autocomplete de fornecedor (cadastro) e de produto direto na descricao do item
  const [supSuggest, setSupSuggest] = useState([]);
  const [supOpen, setSupOpen] = useState(false);
  const [descQuery, setDescQuery] = useState(null);               // { idx, q }
  const [descSuggest, setDescSuggest] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${ERP}/payment-methods?scope=purchases`, { withCredentials: true });
        setPaymentMethods(data.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!searchProd || searchProd.length < 2) { setProducts([]); return; }
      try {
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(searchProd)}&limit=15${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        setProducts(data.data || []);
      } catch { setProducts([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchProd, companyId]);

  useEffect(() => {
    if (!supOpen || (supplierName || '').trim().length < 2) { setSupSuggest([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${ERP}/suppliers?search=${encodeURIComponent(supplierName.trim())}&limit=10${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        setSupSuggest(data.data || []);
      } catch { setSupSuggest([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [supplierName, supOpen, companyId]);

  useEffect(() => {
    if (!descQuery || (descQuery.q || '').trim().length < 2) { setDescSuggest([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(descQuery.q.trim())}&limit=10${companyId ? `&company_id=${companyId}` : ''}`, { withCredentials: true });
        setDescSuggest(data.data || []);
      } catch { setDescSuggest([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [descQuery, companyId]);

  const pickSupplier = (sp) => {
    setSupplierName(sp.name || '');
    if (sp.document) setSupplierCnpj(sp.document);
    if (sp.transp_ie) setSupplierIE(sp.transp_ie);
    setSupOpen(false); setSupSuggest([]);
  };

  // iter156ev: cadastro rapido de item a partir da compra — ao salvar, preenche/vincula a linha
  const [quickProduct, setQuickProduct] = useState(null);              // { idx, initial }
  const [quickSupplier, setQuickSupplier] = useState(null);            // iter156ew: { name, document, ie }
  const openQuickProduct = () => {
    let idx = items.findIndex(it => !it.product_id);
    if (idx < 0) { addLine(); idx = items.length; }
    const it = items[idx] || {};
    setQuickProduct({ idx, initial: { name: it.src_descricao || '', sku: it.src_codigo || '', ean: it.src_ean || '', unit: it.src_unidade || 'UN', cost_price: Number(it.unit_cost) || 0, price: Number(it.sale_price) || 0, ncm: it.src_ncm || '' } });
  };
  const onQuickProductSaved = async (p) => {
    const idx = quickProduct.idx;
    setQuickProduct(null);
    await pickProductForRow(idx, p);
    toast.success(`Item "${p.name}" cadastrado e vinculado a linha ${idx + 1}`);
  };

  const pickProductForRow = async (idx, p) => {
    setDescQuery(null); setDescSuggest([]);
    await conciliate(idx, 'link', p.id, { sale_price: p.price ?? null });
    setItems(prev => prev.map((it, i) => i === idx ? {
      ...it,
      src_descricao: p.name || it.src_descricao,
      src_codigo: it.src_codigo || p.sku || '',
      src_ean: it.src_ean || p.ean || '',
      unit_cost: Number(it.unit_cost) > 0 ? it.unit_cost : (Number(p.cost_price) || 0),
    } : it));
  };

  const subtotal = useMemo(() => items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_cost) || 0), 0), [items]);
  const total = subtotal - Number(discount || 0) + Number(freight || 0) + Number(otherCharges || 0);
  const unmatched = items.filter(it => !it.product_id).length;

  const updItem = (idx, k, v) => {
    setItems(prev => {
      const copy = [...prev];
      let val = v;
      if (k === 'quantity' || k === 'unit_cost') val = Number(v) || 0;
      const next = { ...copy[idx], [k]: val };
      // iter156e/f: ao alterar sale_price em item VINCULADO, so marca update=true quando
      // o valor for DIFERENTE do preco original do produto (prefilled). Assim se o usuario
      // nao mexer, nao atualiza o preco do produto (opcao 'a' escolhida).
      if (k === 'sale_price') {
        const isLinked = !!next.product_id && next.match_status !== 'new';
        if (isLinked) {
          const orig = next.prefilled_sale_price;
          const curr = val == null || val === '' ? null : Number(val);
          if (curr === null) next.update_sale_price = false;
          else if (orig == null) next.update_sale_price = curr > 0;
          else next.update_sale_price = Math.abs(curr - Number(orig)) > 0.005; // diff > 0.5 centavos
        }
      }
      copy[idx] = next;
      return copy;
    });
  };

  const addLine = () => {
    setItems(prev => [...prev, { src_codigo: '', src_descricao: '', src_ean: '', src_unidade: 'UN', quantity: 1, unit_cost: 0, product_id: null, match_status: 'unmatched' }]);
  };

  const rmItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const draftPayload = (itemsOverride = null) => ({
    company_id: companyId,
    supplier_name: supplierName, supplier_cnpj: supplierCnpj, supplier_ie: supplierIE,
    nf_chave: draft.nf_chave, nf_numero: nfNum, nf_serie: nfSerie, nf_emit_date: draft.nf_emit_date,
    source: draft.source || 'manual', manifesto_doc_id: draft.manifesto_doc_id,
    items: itemsOverride || items, discount, freight, other_charges: otherCharges, payments, notes,
  });

  // iter156ew: sempre sincroniza o rascunho no servidor (POST na 1a vez, PUT depois) para que
  // linhas adicionadas localmente existam no backend antes do conciliate-item ("item_index invalido").
  const ensureSaved = async (itemsOverride = null) => {
    if (draft.id) {
      await axios.put(`${ERP}/purchases/${draft.id}`, draftPayload(itemsOverride), { withCredentials: true });
      return draft.id;
    }
    const { data } = await axios.post(`${ERP}/purchases`, draftPayload(itemsOverride), { withCredentials: true });
    draft.id = data.id;     // mutate so subsequent calls treat as edit
    draft._editing = true;
    return data.id;
  };

  const save = async () => {
    setSaving(true);
    try {
      await ensureSaved();
      toast.success('Rascunho salvo');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  const conciliate = async (idx, action, prodId = null, extra = {}) => {
    try {
      const id = await ensureSaved();
      // iter156e/f: sem checkbox — se o usuario digitou sale_price manualmente, marca "atualizar".
      // No link, o backend nao precisa saber do sale_price pre-preenchido (so mostrar ao usuario).
      const body = action === 'auto'
        ? { item_index: idx, auto_create: true, sale_price: extra.sale_price ?? null }
        : { item_index: idx, product_id: prodId, sale_price: null, update_sale_price: false };
      const { data } = await axios.post(`${ERP}/purchases/${id}/conciliate-item`, body, { withCredentials: true });
      // iter156f: para link, preenche o sale_price no state local com o preco atual do produto
      // (do estoque) para o usuario visualizar. update_sale_price=false ate que o usuario altere.
      const prefillPrice = (action === 'link' && extra.sale_price != null && Number(extra.sale_price) > 0)
        ? Number(extra.sale_price)
        : null;
      const merged = action === 'auto'
        ? data.item
        : { ...data.item, sale_price: prefillPrice, prefilled_sale_price: prefillPrice, update_sale_price: false };
      setItems(prev => prev.map((it, i) => i === idx ? merged : it));
      toast.success(action === 'auto' ? 'Produto auto-cadastrado' : `Produto vinculado${prefillPrice ? ` — preco atual: ${fmtBRL(prefillPrice)}` : ''}`);
      setSearchTargetIdx(null); setSearchProd(''); setProducts([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na conciliacao');
    }
  };

  // iter156d: cadastra em lote TODOS os itens ainda nao vinculados como produtos novos.
  const bulkAutoCreate = async () => {
    const markup = window.prompt('Cadastrar TODOS os itens nao vinculados como novos produtos.\n\nMarkup padrao sobre o custo (%) para o preco de venda:', '30');
    if (markup === null) return;
    const markupPct = Math.max(0, Number(markup) || 0);
    if (!(await confirmDialog({
      title: `Cadastrar ${unmatched} item(ns) como NOVOS produtos?`,
      message: `Sera aplicado o markup padrao de ${markupPct}% sobre o custo para calcular o preco de venda.`,
      description: 'Cada item nao vinculado sera criado no cadastro de produtos e ja aparecera nesta compra vinculado. Voce pode ajustar precos e demais campos depois.',
      confirmLabel: 'Cadastrar todos',
      variant: 'info',
    }))) return;
    const tid = toast.loading('Cadastrando...');
    try {
      const id = await ensureSaved();
      const { data } = await axios.post(`${ERP}/purchases/${id}/bulk-auto-create`, { default_markup_pct: markupPct }, { withCredentials: true });
      toast.dismiss(tid);
      setItems(data.items || []);
      toast.success(`${data.created || 0} produto(s) cadastrado(s)`);
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro no cadastro em lote');
    }
  };

  const finalize = async () => {
    if (unmatched > 0) { toast.error(`Concilie ${unmatched} item(ns) antes de finalizar`); setStep('itens'); return; }
    if (payments.length === 0) { toast.error('Adicione ao menos uma forma de pagamento'); setStep('pagamentos'); return; }
    // iter156d/e: se houver itens SEM preco de venda (ou <= 0), oferece markup em massa.
    // Considera "sem preco" apenas os itens NOVOS ou VINCULADOS com preco vazio (nao os
    // vinculados que o usuario deixou vazio por design = manter preco atual).
    const itemsMissingPrice = items.filter(it => {
      if (!it.product_id) return false; // ignora nao-vinculados (bloqueia finalize antes)
      if (it.match_status === 'new') return !it.sale_price || Number(it.sale_price) <= 0;
      // vinculado: so alerta se o usuario DIGITOU um preco invalido; se em branco = manter preco atual (OK)
      return false;
    });
    if (itemsMissingPrice.length > 0) {
      const markup = window.prompt(`${itemsMissingPrice.length} produto(s) NOVO(s) sem preco de venda definido.\n\nInforme um markup (%) sobre o custo para aplicar automaticamente,\nou clique CANCELAR para voltar e definir manualmente.`, '30');
      if (markup === null) { setStep('itens'); return; }
      const markupPct = Math.max(0, Number(markup) || 0);
      const factor = 1 + (markupPct / 100);
      setItems(prev => prev.map(it => {
        if (it.product_id && it.match_status === 'new' && (!it.sale_price || Number(it.sale_price) <= 0)) {
          const cost = Number(it.unit_cost) || 0;
          return { ...it, sale_price: cost > 0 ? Math.round(cost * factor * 100) / 100 : 0, update_sale_price: true };
        }
        return it;
      }));
      await new Promise(r => setTimeout(r, 100));
    }
    if (!(await confirmDialog({
      title: 'Finalizar esta compra?',
      message: `${items.length} item(ns) serao lancados no estoque desta filial.`,
      description: '• Movimento de ENTRADA no estoque desta filial\n• Atualiza custo e (opcionalmente) preco de venda\n• Gera contas a PAGAR conforme as formas de pagamento\n• Apos finalizar, a compra NAO podera mais ser editada',
      confirmLabel: 'Confirmar e Finalizar',
      variant: 'success',
    }))) return;
    setSaving(true);
    const tid = toast.loading('Finalizando...');
    try {
      const id = await ensureSaved();
      // Save current state first (includes updated sale_price after markup prompt)
      await axios.put(`${ERP}/purchases/${id}`, {
        company_id: companyId, supplier_name: supplierName, supplier_cnpj: supplierCnpj, supplier_ie: supplierIE,
        nf_chave: draft.nf_chave, nf_numero: nfNum, nf_serie: nfSerie, nf_emit_date: draft.nf_emit_date,
        source: draft.source || 'manual', manifesto_doc_id: draft.manifesto_doc_id,
        items, discount, freight, other_charges: otherCharges, payments, notes,
      }, { withCredentials: true });
      await axios.post(`${ERP}/purchases/${id}/finalize`, {}, { withCredentials: true });
      toast.dismiss(tid);
      toast.success(`✅ Compra finalizada! ${items.length} item(ns) entraram no estoque desta filial · ${payments.length ? payments.length + ' conta(s) a pagar geradas' : 'sem contas a pagar'}`);
      onSaved();
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Erro ao finalizar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" data-testid="purchase-form-modal">
      <div className="bg-white w-full md:max-w-5xl md:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        <div className="bg-gradient-to-r from-violet-600 to-cyan-500 px-5 py-4 text-white flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{isFinalized ? 'Compra Finalizada' : (isEdit ? 'Editar' : 'Nova')} Compra</p>
            <p className="text-xl font-black">{supplierName || 'Sem fornecedor'} · {fmtBRL(total)}</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => setStep('itens')} className={`px-3 py-1 text-[10px] font-bold rounded ${step === 'itens' ? 'bg-white text-violet-700' : 'bg-white/20'}`} data-testid="step-itens">1. Itens {unmatched > 0 && `(${unmatched} a conciliar)`}</button>
              <button onClick={() => setStep('pagamentos')} className={`px-3 py-1 text-[10px] font-bold rounded ${step === 'pagamentos' ? 'bg-white text-violet-700' : 'bg-white/20'}`} data-testid="step-pagamentos">2. Pagamentos</button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="purchase-close"><X /></button>
        </div>

        <div className="overflow-y-auto p-5 space-y-4 flex-1">
          {/* Fornecedor + NF */}
          <div className="border border-slate-200 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <label className={labelCls + ' inline-flex items-center gap-1.5'}>
                Fornecedor *
                {!isFinalized && (
                  <button type="button" onClick={() => setQuickSupplier({ name: supplierName, document: supplierCnpj, ie: supplierIE })} title="Cadastro rapido de fornecedor (preenche o campo ao salvar)" className="p-0.5 rounded text-violet-600 hover:bg-violet-100" data-testid="purchase-quick-supplier-btn">
                    <UserPlus size={14} />
                  </button>
                )}
              </label>
              {quickSupplier && <QuickSupplierModal initial={quickSupplier} onClose={() => setQuickSupplier(null)} onSaved={(sp) => { setQuickSupplier(null); pickSupplier(sp); toast.success(`Fornecedor "${sp.name}" cadastrado`); }} />}
              <div className="relative">
                <input value={supplierName} onChange={e => { setSupplierName(e.target.value); setSupOpen(true); }} onFocus={() => setSupOpen(true)} onBlur={() => setTimeout(() => setSupOpen(false), 150)} placeholder="Digite para buscar fornecedor cadastrado..." className={inputClass} disabled={isFinalized} autoComplete="off" data-testid="purchase-supplier-name" />
                {supOpen && supSuggest.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="purchase-supplier-suggestions">
                    {supSuggest.map(sp => (
                      <button key={sp.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickSupplier(sp)} className="w-full text-left px-3 py-1.5 hover:bg-violet-50 text-xs flex justify-between gap-2" data-testid={`purchase-supplier-opt-${sp.id}`}>
                        <span className="truncate font-semibold text-slate-800">{sp.name}</span>
                        <span className="text-slate-400 whitespace-nowrap">{sp.document || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>CNPJ</label>
              <input value={supplierCnpj} onChange={e => setSupplierCnpj(e.target.value)} className={inputClass} disabled={isFinalized} data-testid="purchase-supplier-cnpj" />
            </div>
            <div>
              <label className={labelCls}>IE</label>
              <input value={supplierIE} onChange={e => setSupplierIE(e.target.value)} className={inputClass} disabled={isFinalized} />
            </div>
            <div>
              <label className={labelCls}>NF Numero</label>
              <input value={nfNum} onChange={e => setNfNum(e.target.value)} className={inputClass} disabled={isFinalized} />
            </div>
            <div>
              <label className={labelCls}>Serie</label>
              <input value={nfSerie} onChange={e => setNfSerie(e.target.value)} className={inputClass} disabled={isFinalized} />
            </div>
            {draft.nf_chave && (
              <div className="md:col-span-4">
                <label className={labelCls}>Chave de Acesso</label>
                <p className="text-[10px] font-mono text-slate-500">{draft.nf_chave}</p>
              </div>
            )}
          </div>

          {/* Step Itens */}
          {step === 'itens' && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs font-black text-slate-500 uppercase">Itens da Compra</p>
                <div className="flex items-center gap-2">
                  {!isFinalized && unmatched >= 2 && (
                    <button onClick={bulkAutoCreate} className="text-[11px] font-bold text-white bg-violet-600 hover:bg-violet-700 px-2.5 py-1 rounded flex items-center gap-1" data-testid="bulk-auto-create-btn">
                      <Sparkles size={12} /> Cadastrar {unmatched} como novos
                    </button>
                  )}
                  {!isFinalized && <button onClick={addLine} className="text-xs font-bold text-violet-600 flex items-center gap-1"><Plus size={12} /> Linha</button>}
                </div>
              </div>
              {items.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Adicione itens manualmente ou importe um XML.</p>
              ) : (
                <div className="overflow-x-auto -mx-4 px-4">
                  {/* iter156e/f: colunas otimizadas — Descricao ampla; inputs monetarios sem spinner
                     e com formatacao pt-BR (12.345,67) automatica. Preco Venda apos Total. */}
                  <table className="w-full text-xs" style={{ minWidth: 800 }}>
                    <colgroup>
                      <col style={{ width: '90px' }} />
                      <col />
                      <col style={{ width: '70px' }} />
                      <col style={{ width: '92px' }} />
                      <col style={{ width: '100px' }} />
                      <col style={{ width: '110px' }} />
                      <col style={{ width: '92px' }} />
                      <col style={{ width: '88px' }} />
                    </colgroup>
                    <thead className="text-slate-500">
                      <tr>
                        <th className="text-left py-1 pr-1">Cod/EAN</th>
                        <th className="text-left py-1 pr-2">
                          <span className="inline-flex items-center gap-1.5">
                            Item
                            {!isFinalized && (
                              <button type="button" onClick={openQuickProduct} title="Cadastro rapido de item (preenche a linha ao salvar)" className="p-0.5 rounded text-violet-600 hover:bg-violet-100" data-testid="purchase-quick-product-btn">
                                <PackagePlus size={14} />
                              </button>
                            )}
                          </span>
                        </th>
                        <th className="text-right py-1 pr-1">Qtd</th>
                        <th className="text-right py-1 pr-1">Custo (R$)</th>
                        <th className="text-right py-1 pr-1">Total (R$)</th>
                        <th className="text-right py-1 pr-1">Preco Venda</th>
                        <th className="text-left py-1 pr-1">Status</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((it, idx) => {
                        const isUnmatched = !it.product_id;
                        const isNew = it.match_status === 'new';
                        const rowClass = isUnmatched ? 'bg-amber-50/60' : '';
                        const qtdVal = Number(it.quantity) || 0;
                        const custoVal = Number(it.unit_cost) || 0;
                        return (
                          <React.Fragment key={idx}>
                            <tr className={rowClass} data-testid={`purchase-item-row-${idx}`}>
                              <td className="py-1.5 pr-1 align-middle">
                                <p className="font-mono text-[10px] truncate">{it.src_codigo}</p>
                                <p className="font-mono text-[10px] text-slate-400 truncate">{it.src_ean}</p>
                              </td>
                              <td className="py-1.5 pr-2 align-middle">
                                <input value={it.src_descricao} onChange={e => { updItem(idx, 'src_descricao', e.target.value); const r = e.target.getBoundingClientRect(); setDescQuery({ idx, q: e.target.value, top: r.bottom + 4, left: r.left, width: r.width }); }} onBlur={() => setTimeout(() => setDescQuery(q => (q && q.idx === idx ? null : q)), 150)} placeholder="Digite para buscar produto cadastrado..." className={`${inputClass} text-xs w-full`} disabled={isFinalized} autoComplete="off" data-testid={`item-desc-${idx}`} />
                                {descQuery && descQuery.idx === idx && descSuggest.length > 0 && createPortal(
                                  <div style={{ position: 'fixed', top: descQuery.top, left: descQuery.left, width: Math.max(280, descQuery.width), zIndex: 9999 }} className="bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid={`item-desc-suggestions-${idx}`}>
                                    {descSuggest.map(p => (
                                      <button key={p.id} type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickProductForRow(idx, p)} className="w-full text-left px-3 py-1.5 hover:bg-sky-50 text-xs flex justify-between gap-2" data-testid={`item-desc-opt-${idx}-${p.id}`}>
                                        <span className="truncate text-slate-800">{p.name}</span>
                                        <span className="text-slate-400 whitespace-nowrap">{p.sku || p.ean} · {fmtBRL(p.price || 0)}</span>
                                      </button>
                                    ))}
                                  </div>, document.body)}
                              </td>
                              <td className="py-1.5 pr-1 align-middle">
                                {/* Qtd: aceita inteiro OU decimal; sem spinner */}
                                <BRLInput
                                  value={qtdVal}
                                  onChange={(v) => updItem(idx, 'quantity', v ?? 0)}
                                  className={`${inputClass} text-xs text-right px-1.5 py-1 no-spinner`}
                                  disabled={isFinalized}
                                  testid={`item-qty-${idx}`}
                                  ariaLabel="Quantidade"
                                />
                              </td>
                              <td className="py-1.5 pr-1 align-middle">
                                <BRLInput
                                  value={custoVal}
                                  onChange={(v) => updItem(idx, 'unit_cost', v ?? 0)}
                                  className={`${inputClass} text-xs text-right px-1.5 py-1 no-spinner`}
                                  disabled={isFinalized}
                                  testid={`item-cost-${idx}`}
                                  ariaLabel="Custo unitario"
                                />
                              </td>
                              <td className="py-1.5 pr-1 text-right font-bold text-slate-800 align-middle whitespace-nowrap">
                                {fmtBRL(qtdVal * custoVal)}
                              </td>
                              <td className="py-1.5 pr-1 align-middle">
                                <BRLInput
                                  value={it.sale_price}
                                  onChange={(v) => updItem(idx, 'sale_price', v)}
                                  prefix="R$"
                                  placeholder={isUnmatched ? '—' : (isNew ? 'Novo' : 'Atual')}
                                  className={`${inputClass} text-xs text-right px-1.5 py-1 no-spinner ${!it.sale_price && !isUnmatched && !isNew ? 'bg-slate-50' : ''}`}
                                  disabled={isFinalized || isUnmatched}
                                  testid={`item-sale-price-${idx}`}
                                  allowEmpty
                                  ariaLabel="Preco de venda"
                                />
                              </td>
                              <td className="py-1.5 pr-1 align-middle">
                                {isUnmatched ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 whitespace-nowrap">
                                    <AlertCircle size={10} /> Nao vinc.
                                  </span>
                                ) : isNew ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 whitespace-nowrap">
                                    <Sparkles size={10} /> Novo
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 whitespace-nowrap">
                                    <CheckCircle size={10} /> Vinc.
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 pl-1 align-middle text-right">
                                <div className="inline-flex items-center gap-0.5 justify-end">
                                  {isUnmatched && !isFinalized && (
                                    <>
                                      <button onClick={() => { setSearchTargetIdx(idx); setSearchProd(''); }} title="Vincular a produto existente" className="p-1.5 rounded hover:bg-sky-100 text-sky-600 flex-shrink-0" data-testid={`item-link-${idx}`}><Link2 size={12} /></button>
                                      <button onClick={() => conciliate(idx, 'auto', null, { sale_price: it.sale_price ?? null })} title="Auto-cadastrar como novo produto" className="p-1.5 rounded hover:bg-violet-100 text-violet-600 flex-shrink-0" data-testid={`item-auto-${idx}`}><Sparkles size={12} /></button>
                                    </>
                                  )}
                                  {!isFinalized && <button onClick={() => rmItem(idx)} className="p-1.5 rounded hover:bg-rose-100 text-rose-600 flex-shrink-0" data-testid={`item-remove-${idx}`}><Trash2 size={12} /></button>}
                                </div>
                              </td>
                            </tr>
                            {searchTargetIdx === idx && (
                              <tr>
                                <td colSpan={8} className="p-2 bg-sky-50/50">
                                  <div className="relative">
                                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input autoFocus value={searchProd} onChange={e => setSearchProd(e.target.value)} placeholder="Buscar produto cadastrado..." className={`${inputClass} pl-9 text-xs`} data-testid={`item-search-${idx}`} />
                                    {products.length > 0 && (
                                      <div className="mt-1 bg-white border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                                        {products.map(p => (
                                          <button key={p.id} onClick={() => conciliate(idx, 'link', p.id, { sale_price: p.price ?? null })} className="w-full text-left px-3 py-1.5 hover:bg-sky-100 text-xs flex justify-between" data-testid={`item-search-result-${p.id}`}>
                                            <span className="truncate">{p.name}</span>
                                            <span className="text-slate-400">{p.sku || p.ean} · {fmtBRL(p.price || 0)}</span>
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                    <button onClick={() => setSearchTargetIdx(null)} className="absolute right-2 top-2 text-slate-400"><X size={14} /></button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-slate-100">
                <div><label className={labelCls}>Desconto</label><BRLInput value={discount} onChange={v => setDiscount(v ?? 0)} className={`${inputClass} no-spinner`} disabled={isFinalized} testid="purchase-discount" ariaLabel="Desconto" /></div>
                <div><label className={labelCls}>Frete</label><BRLInput value={freight} onChange={v => setFreight(v ?? 0)} className={`${inputClass} no-spinner`} disabled={isFinalized} testid="purchase-freight" ariaLabel="Frete" /></div>
                <div><label className={labelCls}>Outros</label><BRLInput value={otherCharges} onChange={v => setOtherCharges(v ?? 0)} className={`${inputClass} no-spinner`} disabled={isFinalized} testid="purchase-other-charges" ariaLabel="Outros" /></div>
                <div className="bg-violet-50 border border-violet-200 rounded-xl p-2 flex flex-col justify-center">
                  <p className="text-[10px] font-bold text-violet-600 uppercase">Total</p>
                  <p className="text-lg font-black text-violet-800" data-testid="purchase-total">{fmtBRL(total)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Step Pagamentos */}
          {step === 'pagamentos' && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black text-slate-500 uppercase">Pagamentos</p>
                {!isFinalized && (
                  <button onClick={() => setPayments(p => [...p, { payment_method_name: '', amount: total - p.reduce((s, x) => s + Number(x.amount || 0), 0), installments: 1, first_due_date: localDateISO(), interval_days: 30, open: false }])} className="text-xs font-bold text-violet-600 flex items-center gap-1" data-testid="add-payment-btn">
                    <Plus size={12} /> Forma de Pgto
                  </button>
                )}
              </div>
              {payments.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nenhum pagamento. Adicione ao menos um.</p>
              ) : payments.map((p, idx) => (
                <div key={idx} className="bg-slate-50 rounded-xl p-3 space-y-2" data-testid={`payment-row-${idx}`}>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                    <div className="md:col-span-2">
                      <label className={labelCls}>Forma</label>
                      <select value={p.payment_method_name} onChange={e => setPayments(prev => prev.map((x, i) => i === idx ? { ...x, payment_method_name: e.target.value, open: e.target.value.toLowerCase().includes('aberto') } : x))} className={inputClass} disabled={isFinalized}>
                        <option value="">-- Selecione --</option>
                        <option value="Em aberto">Em aberto (sem vencimento)</option>
                        {paymentMethods.map(pm => <option key={pm.id} value={pm.name}>{pm.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Valor</label>
                      <BRLInput
                        value={p.amount}
                        onChange={v => setPayments(prev => prev.map((x, i) => i === idx ? { ...x, amount: Number(v) || 0 } : x))}
                        className={`${inputClass} no-spinner`}
                        disabled={isFinalized}
                        testid={`payment-amount-${idx}`}
                        ariaLabel="Valor do pagamento"
                      />
                    </div>
                    {!p.open && (
                      <>
                        <div>
                          <label className={labelCls}>Parcelas</label>
                          <input type="number" min="1" max="60" value={p.installments} onChange={e => setPayments(prev => prev.map((x, i) => i === idx ? { ...x, installments: Number(e.target.value) || 1 } : x))} className={inputClass} disabled={isFinalized} />
                        </div>
                        <div>
                          <label className={labelCls}>1o Vencimento</label>
                          <input type="date" value={p.first_due_date} onChange={e => setPayments(prev => prev.map((x, i) => i === idx ? { ...x, first_due_date: e.target.value } : x))} className={inputClass} disabled={isFinalized} />
                        </div>
                      </>
                    )}
                    {!isFinalized && (
                      <button onClick={() => setPayments(prev => prev.filter((_, i) => i !== idx))} className="p-2 text-rose-500 hover:bg-rose-100 rounded h-9 self-end"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
              <div className="text-right text-xs text-slate-500">
                Total pagamentos: <span className="font-bold">{fmtBRL(payments.reduce((s, p) => s + Number(p.amount || 0), 0))}</span> / <span className="text-violet-700 font-bold">{fmtBRL(total)}</span>
              </div>
            </div>
          )}

          {quickProduct && <QuickProductModal initial={quickProduct.initial} companyId={companyId} onClose={() => setQuickProduct(null)} onSaved={onQuickProductSaved} />}
          <div>
            <label className={labelCls}>Observacoes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={inputClass} disabled={isFinalized} />
          </div>
        </div>

        <div className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex flex-wrap items-center justify-end gap-2">
          {!isFinalized && (
            <>
              <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-bold">Cancelar</button>
              <button onClick={save} disabled={saving} className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold text-sm flex items-center gap-2" data-testid="purchase-save-btn">
                <Save size={14} /> Salvar rascunho
              </button>
              <button onClick={finalize} disabled={saving} className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-sm flex items-center gap-2 shadow-md" data-testid="purchase-finalize-btn">
                {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> : <Banknote size={14} />}
                Finalizar (estoque + financeiro)
              </button>
            </>
          )}
          {isFinalized && <button onClick={onClose} className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-sm">Fechar</button>}
        </div>
      </div>
    </div>
  );
};


/* ========================= PurchaseRequestsPage =========================
 * Lista de Solicitacoes de Compra (catalogo modo Compra).
 * Tabs: "Solicitacoes" (CRUD) | "Consolidado por Fornecedor" (lista de itens p/ impressao/WhatsApp).
 */
export const PurchaseRequestsPage = ({ companyId }) => {
  const [tab, setTab] = useState('list');
  const [showNew, setShowNew] = useState(false);
  const [listKey, setListKey] = useState(0);
  // iter77ct.23: gate a aba "Sugestao de Compra" pela permissao purchase-requests.view_suggestions
  const [canViewSuggestions, setCanViewSuggestions] = useState(true);
  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (companyId) params.set('company_id', companyId);
    params.set('limit', '1');
    axios.get(`${API}/erp/purchase-requests?${params}`, { withCredentials: true })
      .then(({ data }) => { if (alive) setCanViewSuggestions(data.can_view_suggestions !== false); })
      .catch(() => { if (alive) setCanViewSuggestions(false); });
    return () => { alive = false; };
  }, [companyId]);
  // Se usuario perder permissao enquanto na aba, volta pra lista
  useEffect(() => { if (!canViewSuggestions && tab === 'consolidated') setTab('list'); }, [canViewSuggestions, tab]);
  return (
    <div className="space-y-4" data-testid="prequests-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-black text-slate-900 flex items-center gap-2"><FileText className="text-violet-600" /> Solicitacoes de Compra</h2>
          <p className="text-xs text-slate-500">Pedidos das filiais (sistema ou catalogo) para a Matriz comprar. Consolide por fornecedor para enviar via WhatsApp.</p>
        </div>
        <button onClick={() => setShowNew(true)} className="px-4 py-2 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white text-sm font-black shadow-md flex items-center gap-1.5 flex-shrink-0" data-testid="prequest-new-btn">
          <Plus size={16} /> Nova Solicitacao
        </button>
      </div>
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab('list')} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === 'list' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="prequest-tab-list">Solicitacoes</button>
        {canViewSuggestions && (
          <button onClick={() => setTab('consolidated')} className={`px-4 py-2 text-sm font-bold border-b-2 ${tab === 'consolidated' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid="prequest-tab-consolidated">Sugestao de Compra</button>
        )}
      </div>
      {tab === 'list' ? <PRequestList key={listKey} companyId={companyId} /> : (canViewSuggestions ? <PRequestConsolidated companyId={companyId} /> : null)}
      {showNew && (
        <PRequestNewModal companyId={companyId} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); setListKey(k => k + 1); setTab('list'); }} />
      )}
    </div>
  );
};

/* --------- Lista de Solicitacoes (CRUD) --------- */
const PRequestList = ({ companyId }) => {
  const [rows, setRows] = useState([]);
  const [isMatriz, setIsMatriz] = useState(false);
  const [isPurchaseAdmin, setIsPurchaseAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  // iter77k: filtro de data — default ultimos 3 dias (rapido + custom)
  const todayISO = () => localDateISO();
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localDateISO(d); };
  const [datePreset, setDatePreset] = useState('3d'); // 3d | 7d | 30d | all | custom
  const [dateFrom, setDateFrom] = useState(daysAgo(3));
  const [dateTo, setDateTo] = useState(todayISO());

  const applyPreset = (p) => {
    setDatePreset(p);
    if (p === '3d') { setDateFrom(daysAgo(3)); setDateTo(todayISO()); }
    else if (p === '7d') { setDateFrom(daysAgo(7)); setDateTo(todayISO()); }
    else if (p === '30d') { setDateFrom(daysAgo(30)); setDateTo(todayISO()); }
    else if (p === 'all') { setDateFrom(''); setDateTo(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = new URLSearchParams();
      if (companyId) qp.set('company_id', companyId);
      if (dateFrom) qp.set('date_from', dateFrom);
      if (dateTo) qp.set('date_to', dateTo);
      const qs = qp.toString();
      const { data } = await axios.get(`${ERP}/purchase-requests${qs ? `?${qs}` : ''}`, { withCredentials: true });
      setRows(data.data || []);
      setIsMatriz(!!data.is_matriz_user);
      setIsPurchaseAdmin(!!data.is_purchase_admin);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [companyId, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const send = async (id) => {
    try {
      await axios.post(`${ERP}/purchase-requests/${id}/send`, {}, { withCredentials: true });
      toast.success('Enviada para a matriz');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const approve = async (id) => {
    try {
      await axios.post(`${ERP}/purchase-requests/${id}/approve`, {}, { withCredentials: true });
      toast.success('Aprovada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  const remove = async (id) => {
    if (!(await confirmDialog({ title: 'Excluir esta solicitacao? Esta acao nao pode ser desfeita.', confirmLabel: 'Confirmar', danger: true }))) return;
    try {
      await axios.delete(`${ERP}/purchase-requests/${id}`, { withCredentials: true });
      toast.success('Excluida');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  // iter77da: abre o PDF da solicitacao numa nova aba para impressao/download.
  // O navegador exibe o PDF inline com a caixa de dialogo de impressao pronta.
  const printPDF = async (id) => {
    try {
      const { data: blob } = await axios.get(`${ERP}/purchase-requests/${id}/pdf`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        // popup bloqueado: forca download
        const a = document.createElement('a');
        a.href = url;
        a.download = `solicitacao-compra-${String(id).slice(-6).toUpperCase()}.pdf`;
        a.click();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar PDF');
    }
  };

  // iter77aa: gera UMA UNICA transferencia (matriz -> filial solicitante) com TODOS os itens
  const [generatingId, setGeneratingId] = useState(null);
  const generateTransferFromRequest = async (id, filialLabel) => {
    if (!(await confirmDialog({ title: `Gerar transferencia da matriz para ${filialLabel}?\n\nA transferencia ficara como "Pendente de envio" e podera ser editada antes do envio.`, confirmLabel: 'Confirmar', danger: false }))) return;
    setGeneratingId(id);
    try {
      const { data } = await axios.post(`${ERP}/purchase-requests/${id}/generate-transfer`, {}, { withCredentials: true });
      toast.success(`Transferencia criada com ${data.items_count} itens (pendente de envio)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar transferencia');
    } finally { setGeneratingId(null); }
  };

  return (
    <div className="space-y-3">
      {/* iter77k: barra de filtro de data */}
      <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="prequest-date-filter">
        <Calendar size={14} className="text-violet-600 flex-shrink-0" />
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Periodo:</span>
        {[
          { k: '3d', label: 'Ult. 3 dias' },
          { k: '7d', label: '7 dias' },
          { k: '30d', label: '30 dias' },
          { k: 'all', label: 'Todos' },
          { k: 'custom', label: 'Personalizado' },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => applyPreset(k)}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition ${datePreset === k ? 'bg-violet-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            data-testid={`prequest-date-preset-${k}`}
          >
            {label}
          </button>
        ))}
        {datePreset === 'custom' && (
          <>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-lg text-[11px]" data-testid="prequest-date-from" />
            <span className="text-[11px] text-slate-400">ate</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-2 py-1 border border-slate-300 rounded-lg text-[11px]" data-testid="prequest-date-to" />
          </>
        )}
        <span className="ml-auto text-[11px] text-slate-400">{rows.length} resultado{rows.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
      {loading ? <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" /></div>
      : rows.length === 0 ? (
        <div className="p-10 text-center" data-testid="prequests-empty">
          <FileText size={48} className="mx-auto text-slate-300 mb-3" />
          <p className="text-sm text-slate-500">Nenhuma solicitacao no periodo selecionado.</p>
          <p className="text-[11px] text-slate-400 mt-1">Mude o periodo acima ou crie uma nova solicitacao.</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Filial</th>
              <th className="text-left px-3 py-2">Itens</th>
              <th className="text-left px-3 py-2">Notas</th>
              <th className="text-left px-3 py-2">Criada</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Acoes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => {
              const filialLabel = r.company_name ? `${r.company_code ? `${r.company_code} | ` : ''}${r.company_name}` : (r.company_id?.slice(0, 8) || '—');
              const isCatalog = r.source === 'catalogo';
              const isPending = (r.status || 'pendente') === 'pendente';
              const rawStatus = isCatalog && isPending ? 'enviada' : (r.status || 'pendente');
              // iter77ag: contextual label — Matriz ve "recebida" quando a filial envia.
              // A filial criadora continua vendo "enviada" (mesma row no banco, label diferente por viewer).
              const statusLabel = (isMatriz && rawStatus === 'enviada') ? 'recebida' : rawStatus;
              // iter77f: filial pode editar enquanto a matriz nao confirmou (rascunho|pendente|enviada).
              // iter77m: usuario com permissao purchase_requests.admin edita/exclui em QUALQUER status.
              const canEdit = isPurchaseAdmin || ['rascunho', 'pendente', 'enviada'].includes(r.status || 'pendente');
              const canApprove = isMatriz && ['enviada', 'pendente'].includes(r.status || 'pendente');
              const canDelete = isPurchaseAdmin || ['rascunho', 'pendente'].includes(r.status || 'pendente');
              // iter77aa: matriz pode gerar transferencia apos aprovacao; uma vez gerada, vira indicador visual
              const canGenerateTransfer = isMatriz && (r.status === 'aprovada' || r.status === 'enviada') && !r.transfer_id;
              const hasTransfer = !!r.transfer_id;
              return (
                <tr key={r.id} className="hover:bg-violet-50/40" data-testid={`prequest-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs">
                    <p className="font-bold text-slate-800 flex items-center gap-1.5">
                      {/* iter77aa: bolinha azul sinaliza que a solicitacao ja virou transferencia */}
                      {hasTransfer && <span className="inline-block w-2 h-2 rounded-full bg-sky-500" title="Transferencia ja gerada" data-testid={`prequest-transfer-dot-${r.id}`}></span>}
                      {filialLabel}
                    </p>
                    {isCatalog && <span className="text-[10px] text-violet-600 font-mono">via catalogo</span>}
                    {hasTransfer && <span className="text-[10px] text-sky-700 font-bold">Transferencia gerada</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{(r.items || []).length} itens</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{(r.notes || '').slice(0, 60)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${r.status === 'aprovada' ? 'bg-emerald-100 text-emerald-700' : statusLabel === 'recebida' ? 'bg-violet-100 text-violet-700' : statusLabel === 'enviada' ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-700'}`}>{statusLabel}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      {canEdit && (
                        <button onClick={() => setEditing(r)} title="Editar" className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-100 transition" data-testid={`prequest-edit-${r.id}`}>
                          <Edit2 size={14} />
                        </button>
                      )}
                      {/* iter77da: imprimir / exportar PDF da solicitacao */}
                      <button onClick={() => printPDF(r.id)} title="Imprimir / Exportar PDF" className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-100 transition" data-testid={`prequest-pdf-${r.id}`}>
                        <Printer size={14} />
                      </button>
                      {canApprove && (
                        <button onClick={() => approve(r.id)} title="Aprovar" className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-100 transition" data-testid={`prequest-approve-${r.id}`}>
                          <Check size={14} />
                        </button>
                      )}
                      {/* iter77aa: botao gerar transferencia desta solicitacao -> matriz envia para a filial */}
                      {canGenerateTransfer && (
                        <button
                          onClick={() => generateTransferFromRequest(r.id, filialLabel)}
                          disabled={generatingId === r.id}
                          title="Gerar transferencia matriz -> filial (todos os itens)"
                          className="p-1.5 rounded-lg text-sky-700 hover:bg-sky-100 disabled:opacity-40 transition"
                          data-testid={`prequest-gen-transfer-${r.id}`}
                        >
                          <ArrowRightLeft size={14} className={generatingId === r.id ? 'animate-pulse' : ''} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => remove(r.id)} title="Excluir" className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-100 transition" data-testid={`prequest-delete-${r.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      </div>
      {editing && (
        <PRequestNewModal companyId={editing.company_id} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
};


/* --------- PRequestNewModal — Modal MODERNO unificado (iter77 + iter77e: suporta CRIAR e EDITAR) --------- */
const PRequestNewModal = ({ companyId, existing, onClose, onSaved }) => {
  const isEdit = !!existing;
  const editStatus = existing?.status || 'rascunho';
  // Apenas rascunho/pendente permite "Salvar e Enviar". Em 'enviada' a filial pode ajustar
  // mas nao pode reenviar (matriz ainda nao aprovou).
  const canSendNow = !isEdit || editStatus === 'rascunho' || editStatus === 'pendente';
  const [matrizName, setMatrizName] = useState('—');
  const [matrizCode, setMatrizCode] = useState('');
  const [matrizCompanyId, setMatrizCompanyId] = useState('');
  const [requesterUserName, setRequesterUserName] = useState('');
  const [filialName, setFilialName] = useState('');
  const [items, setItems] = useState(() => {
    if (!isEdit) return [];
    return (existing.items || []).map(it => ({
      product_id: it.product_id || null,
      name: it.name || '',
      sku: it.sku || '',
      quantity: Number(it.quantity || 1),
      notes: it.notes || '',
    }));
  });
  const [notes, setNotes] = useState(existing?.notes || '');
  // search
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load: matriz (iter77c: prioriza is_matriz; fallback legacy settings), me (user), companies (filial name)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const effCompanyId = isEdit ? (existing.company_id || companyId) : companyId;
        const [cs, me, cs2] = await Promise.all([
          axios.get(`${ERP}/company-settings?company_id=${effCompanyId || ''}`, { withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(`${API}/auth/me`, { withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(`${ERP}/companies`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        ]);
        if (!alive) return;
        const comps = cs2.data.data || cs2.data || [];
        // 1) Prioridade: empresa com is_matriz=true na lista visivel ao usuario
        let matrizId = '';
        const flagged = comps.find(c => c.is_matriz === true);
        if (flagged) {
          matrizId = flagged.id || flagged._id;
          setMatrizName(flagged.name || '');
          setMatrizCode(flagged.code || '');
        } else {
          // iter77ct.9: usuario pode NAO ter acesso a matriz (allowed_company_ids) —
          // busca a referencia da matriz num endpoint dedicado que ignora restricoes de acesso.
          try {
            const { data: ref } = await axios.get(`${ERP}/companies/matriz-ref`, { withCredentials: true });
            if (alive && ref?.data?.id) {
              matrizId = ref.data.id;
              setMatrizName(ref.data.name || '');
              setMatrizCode(ref.data.code || '');
            }
          } catch { /* silent — fallback abaixo */ }
          if (!matrizId) {
            // 2) Fallback legacy: settings.purchase_request_target_company_id
            matrizId = (cs.data || {}).purchase_request_target_company_id || '';
            if (matrizId) {
              const m = comps.find(c => (c.id || c._id) === matrizId);
              if (m) { setMatrizName(m.name || ''); setMatrizCode(m.code || ''); }
            }
          }
        }
        setMatrizCompanyId(matrizId);
        if (effCompanyId) {
          const f = comps.find(c => (c.id || c._id) === effCompanyId);
          if (f) setFilialName(`${f.code ? `${f.code} | ` : ''}${f.name || ''}`);
        }
        setRequesterUserName(isEdit ? (existing.requester_user_name || me.data?.name || '') : (me.data?.name || ''));
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [companyId, isEdit, existing]);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const effCompanyId = isEdit ? (existing.company_id || companyId) : companyId;
        const { data } = await axios.get(`${ERP}/pdv/products?q=${encodeURIComponent(search)}&company_id=${effCompanyId || ''}&limit=12`, { withCredentials: true });
        setResults(data.data || []);
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [search, companyId, isEdit, existing]);

  // iter77h: produto "em espera" (staged) — ocupa o lado direito da barra de busca com qty inline.
  const qtyInlineRef = useRef(null);
  const searchRef = useRef(null);
  const [pending, setPending] = useState(null); // {product_id, name, sku, quantity}

  // Carrega o produto no slot pendente (qty inline). Se ja existe no carrinho, soma 1.
  const stageProduct = (p) => {
    setSearch(''); setResults([]);
    setPending({ product_id: p.id, name: p.name, sku: p.sku, quantity: 1 });
    // foca o input inline de quantidade no proximo tick
    setTimeout(() => {
      if (qtyInlineRef.current) { qtyInlineRef.current.focus(); qtyInlineRef.current.select(); }
    }, 30);
  };

  // Commit do pendente: adiciona/mescla no carrinho e devolve foco para a busca.
  const commitPending = () => {
    if (!pending) return;
    const qty = Math.max(0.01, Number(pending.quantity) || 1);
    setItems(prev => {
      const idx = prev.findIndex(i => i.product_id === pending.product_id);
      if (idx >= 0) {
        return prev.map((i, j) => j === idx ? { ...i, quantity: (Number(i.quantity) || 0) + qty } : i);
      }
      return [...prev, { product_id: pending.product_id, name: pending.name, sku: pending.sku, quantity: qty }];
    });
    setPending(null);
    setTimeout(() => { if (searchRef.current) searchRef.current.focus(); }, 30);
  };

  const cancelPending = () => {
    setPending(null);
    setTimeout(() => { if (searchRef.current) searchRef.current.focus(); }, 30);
  };

  // Enter na busca: se ha resultados, "stage" o primeiro; depois usuario ajusta qty e da Enter de novo
  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      stageProduct(results[0]);
    }
  };
  // Enter no qty inline = commit
  const onQtyInlineKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitPending();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelPending();
    }
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateQty = (idx, q) => setItems(items.map((it, i) => i === idx ? { ...it, quantity: Math.max(0.01, Number(q) || 1) } : it));

  const save = async (sendNow) => {
    const effCompanyId = isEdit ? (existing.company_id || companyId) : companyId;
    if (!effCompanyId) { toast.error('Selecione a filial'); return; }
    if (items.length === 0) { toast.error('Adicione ao menos um item'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        // PUT mantém status atual; se sendNow=true e status=rascunho, faz PUT+/send
        await axios.put(`${ERP}/purchase-requests/${existing.id}`, {
          company_id: effCompanyId,
          target_company_id: matrizCompanyId || existing.target_company_id || null,
          items,
          notes,
        }, { withCredentials: true });
        if (sendNow && canSendNow) {
          try {
            await axios.post(`${ERP}/purchase-requests/${existing.id}/send`, {}, { withCredentials: true });
            toast.success('Solicitacao atualizada e enviada para a matriz!');
          } catch (err2) {
            toast.error(err2.response?.data?.detail || 'Salvou, mas falhou ao enviar');
          }
        } else {
          toast.success('Solicitacao atualizada');
        }
      } else {
        await axios.post(`${ERP}/purchase-requests`, {
          company_id: effCompanyId,
          target_company_id: matrizCompanyId || null,
          items,
          notes,
          send_now: !!sendNow,
        }, { withCredentials: true });
        toast.success(sendNow ? 'Solicitacao enviada para a matriz!' : 'Rascunho salvo');
      }
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="prequest-new-modal">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-violet-50 to-fuchsia-50">
          <div>
            <h3 className="text-base font-black text-violet-900 flex items-center gap-2"><Plus size={16} /> {isEdit ? 'Editar Solicitacao de Compra' : 'Nova Solicitacao de Compra'}</h3>
            <p className="text-[11px] text-violet-700">{isEdit ? `Status atual: ${editStatus}. Ajuste os itens e salve, ou salve e envie.` : 'Liste os itens que sua filial precisa. A matriz recebera para comprar.'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-violet-100" data-testid="prequest-new-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Header autopreenchido */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-xl p-3">
            <div>
              <p className="text-[10px] font-bold uppercase text-slate-500">Filial Solicitante</p>
              <p className="text-sm font-black text-slate-800 truncate" data-testid="prequest-filial-name">{filialName || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase text-slate-500">Matriz Destino</p>
              <p className="text-sm font-black text-violet-700 truncate" data-testid="prequest-matriz-name">{matrizCode ? `${matrizCode} | ` : ''}{matrizName}</p>
              {!matrizCompanyId && <p className="text-[10px] text-amber-600 mt-0.5">! Marque uma empresa como Matriz no cadastro</p>}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase text-slate-500">Solicitante (usuario)</p>
              <p className="text-sm font-black text-slate-800 truncate" data-testid="prequest-user-name">{requesterUserName || '—'}</p>
            </div>
          </div>

          {/* Busca de itens — iter77h: qty inline a DIREITA da busca */}
          <div>
            <label className={labelCls}>Buscar Item (codigo, SKU ou nome) — Enter no produto, depois Enter na qtd</label>
            <div className="flex gap-2 items-stretch">
              {/* Busca */}
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                {pending ? (
                  <div className="w-full pl-9 pr-3 py-2 bg-violet-50 border-2 border-violet-400 rounded-xl text-sm font-medium text-violet-900 truncate" data-testid="prequest-pending">
                    {pending.name}<span className="text-[10px] text-violet-600 ml-2">({pending.sku || '—'})</span>
                  </div>
                ) : (
                  <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)} onKeyDown={onSearchKeyDown} placeholder="Ex: 7891234567890 ou DIAZE..." className={`${inputClass} pl-9`} data-testid="prequest-search" autoFocus />
                )}
                {!pending && results.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl z-10 max-h-64 overflow-y-auto" data-testid="prequest-results">
                    {results.map((p, idx) => (
                      <button key={p.id} onClick={() => stageProduct(p)} className={`w-full text-left px-3 py-2.5 hover:bg-violet-50 border-b border-slate-100 last:border-0 flex items-center gap-2 ${idx === 0 ? 'bg-violet-50/40' : ''}`} data-testid="prequest-result-item">
                        {p.images?.[0] ? <img src={p.images[0]} className="w-9 h-9 rounded-lg object-cover flex-shrink-0" alt="" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><FileText size={14} className="text-slate-400" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{p.name}{idx === 0 && <span className="ml-2 text-[9px] font-extrabold text-violet-600">(Enter)</span>}</p>
                          <p className="text-[10px] text-slate-500">{p.sku || '—'} · Estoque: {p.stock ?? 0}</p>
                        </div>
                        <Plus size={14} className="text-violet-600 flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* QTD inline (sempre visivel; ativa quando ha produto pendente) */}
              <div className="flex items-stretch gap-1">
                <input
                  ref={qtyInlineRef}
                  type="number" min="0.01" step="any"
                  value={pending ? pending.quantity : ''}
                  onChange={e => setPending(p => p ? { ...p, quantity: Math.max(0.01, Number(e.target.value) || 1) } : p)}
                  onKeyDown={onQtyInlineKeyDown}
                  onFocus={e => e.target.select()}
                  placeholder="QTD"
                  disabled={!pending}
                  className={`w-24 px-2 py-2 border-2 rounded-xl text-center text-lg font-black ${pending ? 'border-violet-600 bg-white text-violet-900' : 'border-slate-200 bg-slate-100 text-slate-400'}`}
                  data-testid="prequest-qty-inline"
                />
                {pending && (
                  <button onClick={commitPending} title="Adicionar (Enter)" className="px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-black flex items-center" data-testid="prequest-commit-pending">
                    <Plus size={16} />
                  </button>
                )}
              </div>
            </div>
            {pending && <p className="text-[10px] text-violet-700 mt-1">Confirme a quantidade e pressione <strong>Enter</strong> para adicionar a lista. <span className="text-slate-500">(Esc cancela)</span></p>}
            {!pending && searching && <p className="text-[10px] text-slate-400 mt-1">Buscando...</p>}
          </div>

          {/* Lista de itens — qtd inline a esquerda, nome a direita */}
          {items.length > 0 && (
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                  <tr><th className="text-left px-3 py-2 w-24">Qtd</th><th className="text-left px-3 py-2">Item</th><th className="px-3 py-2 w-10"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((it, i) => (
                    <tr key={i} data-testid="prequest-cart-row">
                      <td className="px-3 py-2">
                        <input
                          type="number" min="0.01" step="any"
                          value={it.quantity}
                          onChange={e => updateQty(i, e.target.value)}
                          onFocus={e => e.target.select()}
                          className="w-20 px-2 py-1.5 border border-slate-300 focus:border-violet-600 rounded-lg text-center text-sm font-bold text-slate-800"
                          data-testid="prequest-cart-qty"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-sm font-medium text-slate-900 truncate">{it.name}</p>
                        <p className="text-[10px] text-slate-500">{it.sku || ''}</p>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeItem(i)} className="text-rose-500 hover:text-rose-700" data-testid="prequest-cart-remove"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <label className={labelCls}>Observacoes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={inputClass} placeholder="Ex: urgente, prazo de entrega, observacoes p/ matriz..." data-testid="prequest-notes" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2 sticky bottom-0">
          <span className="text-[11px] text-slate-500">{items.length} {items.length === 1 ? 'item' : 'itens'}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-200" data-testid="prequest-cancel">Cancelar</button>
            <button onClick={() => save(false)} disabled={saving || items.length === 0} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 disabled:bg-slate-400 text-white text-sm font-black flex items-center gap-1" data-testid="prequest-save-draft">
              <Save size={14} /> Salvar
            </button>
            {canSendNow && (
              <button onClick={() => save(true)} disabled={saving || items.length === 0} className="px-4 py-2 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 disabled:from-violet-300 text-white text-sm font-black flex items-center gap-1" data-testid="prequest-save-send">
                <Send size={14} /> Salvar e Enviar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


/* --------- PRequestConsolidated (iter77l: renomeado para "Sugestao de Compra") --------- */
const PRequestConsolidated = ({ companyId: _companyId }) => {
  const [data, setData] = useState({ items: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [groupBySupplier, setGroupBySupplier] = useState(true);
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState('enviada');
  // iter77l: filtro multi-empresa + cache de empresas e fornecedores cadastrados
  const [allCompanies, setAllCompanies] = useState([]);
  const [filterCompanies, setFilterCompanies] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [editingQtyKey, setEditingQtyKey] = useState(null);
  const [supplierPickerFor, setSupplierPickerFor] = useState(null); // {product_id, name}

  useEffect(() => {
    (async () => {
      try {
        const [cs, sp] = await Promise.all([
          axios.get(`${ERP}/companies`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
          axios.get(`${ERP}/suppliers?limit=500`, { withCredentials: true }).catch(() => ({ data: { data: [] } })),
        ]);
        setAllCompanies(cs.data.data || cs.data || []);
        setAllSuppliers(sp.data.data || sp.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (supplierFilter) params.set('supplier_id', supplierFilter);
      if (search) params.set('q', search);
      if (filterCompanies.length > 0) params.set('company_ids', filterCompanies.join(','));
      const { data } = await axios.get(`${ERP}/purchase-requests/consolidated?${params}`, { withCredentials: true });
      setData(data || { items: [], suppliers: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao consolidar');
    } finally { setLoading(false); }
  }, [status, supplierFilter, search, filterCompanies]);

  useEffect(() => { load(); }, [load]);

  // iter77l: vincula fornecedor a produto (permanente + atualiza linhas abertas)
  const assignSupplier = async (productId, supplier) => {
    try {
      await axios.post(`${ERP}/purchase-requests/assign-product-supplier`, {
        product_id: productId,
        supplier_id: supplier.id,
        supplier_name: supplier.name || '',
      }, { withCredentials: true });
      toast.success('Fornecedor vinculado ao produto');
      setSupplierPickerFor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  // iter77l: edita qtd geral redistribuindo entre filiais
  const updateTotalQty = async (productId, newTotal) => {
    try {
      const { data } = await axios.post(`${ERP}/purchase-requests/update-item-quantity`, {
        product_id: productId,
        new_total: parseInt(newTotal, 10) || 0,
      }, { withCredentials: true });
      toast.success(`Quantidade redistribuida (${data.new_total} em ${data.lines_updated} linhas)`);
      setEditingQtyKey(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  // iter77l: gera transferencia automatica matriz -> filiais
  const generateTransfer = async (productId, productName) => {
    if (!(await confirmDialog({ title: `Gerar transferencias automaticas para "${productName}"?\n\nA matriz vai transferir as quantidades pedidas para cada filial.`, confirmLabel: 'Confirmar', danger: false }))) return;
    try {
      const { data } = await axios.post(`${ERP}/purchase-requests/generate-transfer`, {
        product_id: productId,
      }, { withCredentials: true });
      toast.success(`${data.items_count} transferencia(s) gerada(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao gerar');
    }
  };

  const toggleFilialFilter = (id) => {
    setFilterCompanies(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const grouped = useMemo(() => {
    if (!groupBySupplier) return [{ supplier: 'Todos', items: data.items }];
    const map = {};
    for (const it of data.items) {
      const k = it.supplier_name || 'Sem fornecedor';
      if (!map[k]) map[k] = [];
      map[k].push(it);
    }
    return Object.entries(map).map(([supplier, items]) => ({ supplier, items }));
  }, [data.items, groupBySupplier]);

  const toggleAll = (groupItems, on) => {
    setSelected(prev => {
      const next = { ...prev };
      groupItems.forEach(it => { const k = it.product_id || it.name; next[k] = on; });
      return next;
    });
  };
  const toggleOne = (it) => {
    const k = it.product_id || it.name;
    setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  };

  const selectedItems = useMemo(() => data.items.filter(it => selected[it.product_id || it.name]), [data.items, selected]);

  const buildMessage = (supplier) => {
    const dt = new Date().toLocaleDateString('pt-BR');
    let msg = `*Pedido de Cotacao/Compra — ${dt}*\n\n`;
    if (supplier) msg += `Fornecedor: *${supplier}*\n\n`;
    msg += `*Itens solicitados:*\n`;
    const list = selectedItems.length > 0 ? selectedItems : (supplier ? data.items.filter(i => (i.supplier_name || 'Sem fornecedor') === supplier) : data.items);
    list.forEach((it, i) => {
      msg += `${i + 1}. ${it.name}${it.sku ? ` (${it.sku})` : ''} — Qtd: *${it.total_quantity}*\n`;
    });
    msg += `\nTotal de itens: ${list.length}\n`;
    return msg;
  };

  const sendWhatsApp = (supplier) => {
    const list = selectedItems.length > 0 ? selectedItems : (supplier ? data.items.filter(i => (i.supplier_name || 'Sem fornecedor') === supplier) : data.items);
    if (list.length === 0) { toast.error('Selecione itens ou aplique filtros'); return; }
    const text = encodeURIComponent(buildMessage(supplier));
    window.open(`https://api.whatsapp.com/send?text=${text}`, '_blank');
  };

  const print = () => {
    const list = selectedItems.length > 0 ? selectedItems : data.items;
    if (list.length === 0) { toast.error('Nada para imprimir'); return; }
    const w = window.open('', '_blank');
    if (!w) { toast.error('Bloqueio de pop-up. Permita pop-ups para imprimir.'); return; }
    const dt = new Date().toLocaleString('pt-BR');
    const grouped2 = {};
    list.forEach(it => {
      const k = it.supplier_name || 'Sem fornecedor';
      if (!grouped2[k]) grouped2[k] = [];
      grouped2[k].push(it);
    });
    let body = '';
    Object.entries(grouped2).forEach(([sup, its]) => {
      body += `<h3 style="margin-top:18px;background:#ede9fe;padding:6px 10px;color:#5b21b6;border-radius:6px;">${sup} (${its.length})</h3>`;
      body += '<table style="width:100%;border-collapse:collapse;font-size:12px;"><thead><tr style="background:#f1f5f9;text-align:left;"><th style="padding:6px;border-bottom:1px solid #e2e8f0;">Item</th><th style="padding:6px;border-bottom:1px solid #e2e8f0;">SKU</th><th style="padding:6px;border-bottom:1px solid #e2e8f0;text-align:right;">Qtd Total</th><th style="padding:6px;border-bottom:1px solid #e2e8f0;">Filiais</th></tr></thead><tbody>';
      its.forEach(it => {
        const filiais = (it.requests || []).map(r => `${r.filial_name || ''}: ${r.quantity}`).join(', ');
        body += `<tr><td style="padding:5px;border-bottom:1px solid #f1f5f9;">${it.name}</td><td style="padding:5px;border-bottom:1px solid #f1f5f9;">${it.sku || ''}</td><td style="padding:5px;border-bottom:1px solid #f1f5f9;text-align:right;font-weight:700;">${it.total_quantity}</td><td style="padding:5px;border-bottom:1px solid #f1f5f9;font-size:10px;color:#64748b;">${filiais}</td></tr>`;
      });
      body += '</tbody></table>';
    });
    w.document.write(`<html><head><title>Solicitacoes de Compra — ${dt}</title><style>body{font-family:Arial,sans-serif;padding:20px;color:#0f172a;}h1{margin:0 0 10px;}h2{margin:0 0 18px;color:#64748b;font-size:13px;font-weight:400;}@media print{button{display:none;}}</style></head><body><h1>Solicitacoes de Compra — Consolidado</h1><h2>${dt} · ${list.length} item(ns)</h2><button onclick="window.print()" style="background:#7c3aed;color:white;padding:8px 16px;border:none;border-radius:6px;font-weight:700;cursor:pointer;margin-bottom:12px;">Imprimir</button>${body}</body></html>`);
    w.document.close();
  };

  const totalSelected = selectedItems.length;

  return (
    <div className="space-y-3" data-testid="prequest-consolidated">
      {/* iter77l: cabecalho com novo titulo */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-black text-slate-800 flex items-center gap-2"><Sparkles size={16} className="text-violet-600" /> Sugestao de Compra</h2>
          <p className="text-[11px] text-slate-500">Itens consolidados das filiais. Vincule fornecedor, ajuste quantidade e gere transferencias automaticas.</p>
        </div>
      </div>

      {/* iter77l: seletor multi de filiais */}
      {allCompanies.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="suggestion-companies-filter">
          <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Filiais:</span>
          <button onClick={() => setFilterCompanies([])} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold ${filterCompanies.length === 0 ? 'bg-violet-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid="suggestion-filter-all">Todas</button>
          {allCompanies.map(c => {
            const cid = c.id || c._id;
            const active = filterCompanies.includes(cid);
            return (
              <button
                key={cid}
                onClick={() => toggleFilialFilter(cid)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition flex items-center gap-1 ${active ? 'bg-violet-600 text-white shadow' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                data-testid={`suggestion-filter-company-${cid}`}
              >
                {c.is_matriz && <Sparkles size={10} />}
                {c.code ? `${c.code} ` : ''}{c.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3 shadow-sm">
        <div className="relative md:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar item ou SKU..." className={`${inputClass} pl-9`} data-testid="consolidated-search" />
        </div>
        <select value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)} className={inputClass} data-testid="consolidated-supplier-filter">
          <option value="">Todos os fornecedores</option>
          {(data.suppliers || []).map(s => <option key={s.id} value={s.id}>{s.name || 'Sem nome'}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} className={inputClass} data-testid="consolidated-status">
          <option value="enviada">Enviadas</option>
          <option value="aprovada">Aprovadas</option>
          <option value="all">Todas</option>
        </select>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
          <input type="checkbox" checked={groupBySupplier} onChange={e => setGroupBySupplier(e.target.checked)} className="w-4 h-4 accent-violet-600" data-testid="consolidated-group-toggle" />
          Agrupar por fornecedor
        </label>
        <div className="flex gap-2">
          <button onClick={print} disabled={data.items.length === 0} className="px-3 py-2 bg-slate-200 hover:bg-slate-300 disabled:bg-slate-100 disabled:text-slate-400 text-slate-800 rounded-lg font-bold text-xs flex items-center gap-2" data-testid="consolidated-print-btn">
            <FileText size={12} /> Imprimir {totalSelected > 0 ? `(${totalSelected})` : ''}
          </button>
          <button onClick={() => sendWhatsApp(null)} disabled={data.items.length === 0} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg font-bold text-xs flex items-center gap-2" data-testid="consolidated-whatsapp-btn">
            <Send size={12} /> WhatsApp {totalSelected > 0 ? `(${totalSelected})` : ''}
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent" /></div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center" data-testid="consolidated-empty">
            <ShoppingCart size={48} className="mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">Nenhum item nas solicitacoes filtradas.</p>
            <p className="text-[11px] text-slate-400 mt-1">Tente outro status ou limpe os filtros.</p>
          </div>
        ) : grouped.map(g => (
          <div key={g.supplier} className="border-b border-slate-100 last:border-b-0">
            <div className="bg-violet-50 px-4 py-2 flex items-center justify-between sticky top-0">
              <div className="flex items-center gap-2">
                <input type="checkbox" onChange={(e) => toggleAll(g.items, e.target.checked)} className="w-4 h-4 accent-violet-600" data-testid={`consolidated-group-check-${g.supplier}`} />
                <p className="text-sm font-black text-violet-800">{g.supplier}</p>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-200 text-violet-800">{g.items.length}</span>
              </div>
              <button onClick={() => sendWhatsApp(g.supplier)} className="text-[11px] font-bold text-emerald-700 hover:underline flex items-center gap-1" data-testid={`consolidated-supplier-whatsapp-${g.supplier}`}>
                <Send size={10} /> Enviar grupo
              </button>
            </div>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {g.items.map(it => {
                  const k = it.product_id || it.name;
                  const checked = !!selected[k];
                  const editing = editingQtyKey === k;
                  return (
                    <tr key={k} className={`hover:bg-violet-50/30 ${checked ? 'bg-violet-50/60' : ''}`} data-testid={`consolidated-row-${k}`}>
                      <td className="px-3 py-2 w-8"><input type="checkbox" checked={checked} onChange={() => toggleOne(it)} className="w-4 h-4 accent-violet-600" data-testid={`consolidated-check-${k}`} /></td>
                      <td className="px-2 py-2">
                        <p className="font-bold text-slate-800 text-xs">{it.name}</p>
                        <p className="text-[10px] text-slate-400 font-mono">{it.sku}</p>
                      </td>
                      <td className="px-2 py-2 text-right w-32">
                        {/* iter77l: qtd geral editavel inline */}
                        {editing ? (
                          <input
                            type="number" min="0" step="1"
                            defaultValue={Math.round(it.total_quantity)}
                            autoFocus
                            onBlur={(e) => updateTotalQty(it.product_id, e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') updateTotalQty(it.product_id, e.target.value); if (e.key === 'Escape') setEditingQtyKey(null); }}
                            className="w-20 px-2 py-1 border-2 border-violet-500 rounded-lg text-right text-base font-black text-violet-900"
                            data-testid={`consolidated-qty-edit-${k}`}
                          />
                        ) : (
                          <button
                            onClick={() => setEditingQtyKey(k)}
                            className="text-lg font-black text-slate-800 hover:text-violet-700 hover:underline decoration-dotted"
                            title="Clique para editar (redistribui entre filiais)"
                            data-testid={`consolidated-qty-display-${k}`}
                          >
                            {Math.round(it.total_quantity)}
                          </button>
                        )}
                      </td>
                      <td className="px-2 py-2 text-[10px] text-slate-500 max-w-xs">
                        {(it.requests || []).slice(0, 3).map((r, i) => <span key={i} className="inline-block bg-slate-100 px-1.5 py-0.5 rounded mr-1">{r.filial_name || '—'}: {Math.round(r.quantity)}</span>)}
                        {(it.requests || []).length > 3 && <span className="text-slate-400">+{it.requests.length - 3}</span>}
                      </td>
                      {/* iter77ag: botao Vincular Fornecedor movido para a direita (linha compacta, estilo moderno) */}
                      <td className="px-2 py-2 text-right whitespace-nowrap w-44">
                        {it.product_id && (
                          <button
                            onClick={() => setSupplierPickerFor({ product_id: it.product_id, name: it.name, current_id: it.supplier_id })}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold border transition ${it.supplier_id
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                              : 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 animate-pulse'}`}
                            title={it.supplier_id ? `Fornecedor: ${it.supplier_name}` : 'Vincular fornecedor a este produto'}
                            data-testid={`consolidated-supplier-btn-${k}`}
                          >
                            <Link2 size={11} />
                            <span className="truncate max-w-[120px]">{it.supplier_name || 'Vincular fornecedor'}</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* iter77l: modal de selecao de fornecedor */}
      {supplierPickerFor && (
        <SupplierPickerModal
          productName={supplierPickerFor.name}
          currentId={supplierPickerFor.current_id}
          suppliers={allSuppliers}
          onSelect={(sup) => assignSupplier(supplierPickerFor.product_id, sup)}
          onClose={() => setSupplierPickerFor(null)}
        />
      )}
    </div>
  );
};

/* --------- SupplierPickerModal (iter77l) --------- */
const SupplierPickerModal = ({ productName, currentId, suppliers, onSelect, onClose }) => {
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return suppliers;
    const ql = q.toLowerCase();
    return suppliers.filter(s => (s.name || '').toLowerCase().includes(ql));
  }, [suppliers, q]);

  const createAndSelect = async () => {
    if (!newName.trim()) { toast.error('Informe o nome'); return; }
    try {
      const { data } = await axios.post(`${ERP}/suppliers`, { name: newName.trim() }, { withCredentials: true });
      onSelect({ id: data.id || data._id, name: data.name || newName });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar fornecedor');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3" data-testid="supplier-picker-modal">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-violet-50 to-fuchsia-50">
          <h3 className="text-base font-black text-violet-900 flex items-center gap-2"><Link2 size={16} /> Vincular Fornecedor</h3>
          <p className="text-[11px] text-violet-700 mt-0.5 truncate">Produto: <strong>{productName}</strong></p>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          {!creating ? (
            <>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar fornecedor..." className={`${inputClass} pl-9`} autoFocus data-testid="supplier-picker-search" />
              </div>
              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <p className="p-3 text-center text-xs text-slate-400">Nenhum fornecedor encontrado</p>
                ) : filtered.map(s => (
                  <button key={s.id || s._id} onClick={() => onSelect({ id: s.id || s._id, name: s.name })} className={`w-full text-left px-3 py-2.5 hover:bg-violet-50 flex items-center justify-between ${(s.id || s._id) === currentId ? 'bg-violet-50' : ''}`} data-testid={`supplier-picker-item-${s.id || s._id}`}>
                    <span className="text-sm font-medium text-slate-800">{s.name || 'Sem nome'}</span>
                    {(s.id || s._id) === currentId && <CheckCircle size={14} className="text-violet-600" />}
                  </button>
                ))}
              </div>
              <button onClick={() => setCreating(true)} className="w-full py-2 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-xs font-bold flex items-center justify-center gap-1" data-testid="supplier-picker-create-btn">
                <Plus size={12} /> Cadastrar Novo Fornecedor
              </button>
            </>
          ) : (
            <>
              <label className="block text-xs font-bold text-slate-600">Nome do Fornecedor</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Ex: Distribuidora X" className={inputClass} autoFocus onKeyDown={e => { if (e.key === 'Enter') createAndSelect(); }} data-testid="supplier-picker-new-name" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
                <button onClick={createAndSelect} className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs font-black" data-testid="supplier-picker-save-new">Cadastrar e Vincular</button>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded" data-testid="supplier-picker-close">Fechar</button>
        </div>
      </div>
    </div>
  );
};

/* ========================= iter77ct.108: SuperAdminDeletePurchaseModal ========================= */
/* Modal detalhado exibido APENAS para SuperAdmin. Permite escolher se a exclusao
   deve cascatar (reverter estoque + apagar financeiro) OU apenas remover o registro
   da compra (deixando estoque e financeiro intactos — util quando ja foram ajustados manualmente). */
const SuperAdminDeletePurchaseModal = ({ purchase, onClose, onConfirm }) => {
  const [cascade, setCascade] = useState(true);
  const [busy, setBusy] = useState(false);
  const isFinalized = purchase.status === 'received' || purchase.status === 'finalized';
  const nfLabel = purchase.nf_numero ? `NF ${purchase.nf_numero}` : `Compra #${purchase.id.slice(-6)}`;
  const itemsCount = (purchase.items || []).length;
  const paymentsCount = (purchase.payments || []).length;

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm(cascade);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="sa-delete-purchase-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-200">
        <div className="bg-gradient-to-r from-rose-600 to-orange-500 text-white px-6 py-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={22} />
            <div className="flex-1">
              <p className="text-[10px] uppercase font-bold opacity-80">Super Admin — Confirmar Exclusao</p>
              <h3 className="text-lg font-black leading-tight">Excluir {nfLabel}?</h3>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white p-1 -mr-1"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div className="bg-slate-50 rounded-lg py-2 px-1 border border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Itens</p>
              <p className="text-lg font-black text-slate-800">{itemsCount}</p>
            </div>
            <div className="bg-slate-50 rounded-lg py-2 px-1 border border-slate-200">
              <p className="text-[10px] font-bold text-slate-500 uppercase">Pagamentos</p>
              <p className="text-lg font-black text-slate-800">{paymentsCount}</p>
            </div>
            <div className={`rounded-lg py-2 px-1 border ${isFinalized ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-[10px] font-bold uppercase ${isFinalized ? 'text-emerald-600' : 'text-amber-600'}`}>Status</p>
              <p className={`text-sm font-black ${isFinalized ? 'text-emerald-700' : 'text-amber-700'}`}>{isFinalized ? 'Recebida' : 'Rascunho'}</p>
            </div>
          </div>

          {isFinalized ? (
            <div className="space-y-3">
              <p className="text-sm font-bold text-slate-700">Esta compra ja foi finalizada. O que fazer com os dados dependentes?</p>
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition hover:bg-rose-50/50" style={{ borderColor: cascade ? '#e11d48' : '#e2e8f0' }} data-testid="sa-delete-cascade-yes">
                <input type="radio" checked={cascade} onChange={() => setCascade(true)} className="mt-0.5 accent-rose-600" />
                <div className="flex-1">
                  <p className="text-sm font-black text-slate-900">Reverter tudo (recomendado)</p>
                  <p className="text-xs text-slate-600 mt-0.5">Reverte o estoque desta filial (subtrai {itemsCount} item(ns)) E apaga as contas a pagar geradas.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition hover:bg-amber-50/50" style={{ borderColor: !cascade ? '#d97706' : '#e2e8f0' }} data-testid="sa-delete-cascade-no">
                <input type="radio" checked={!cascade} onChange={() => setCascade(false)} className="mt-0.5 accent-amber-600" />
                <div className="flex-1">
                  <p className="text-sm font-black text-slate-900">Apenas remover o registro</p>
                  <p className="text-xs text-slate-600 mt-0.5">Mantem o estoque e as contas a pagar como estao. Use se voce ja ajustou manualmente.</p>
                </div>
              </label>
            </div>
          ) : (
            <p className="text-sm text-slate-700">Rascunho sera removido. Nenhum lancamento em estoque ou financeiro foi feito.</p>
          )}

          <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-start gap-2">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <p><strong>Atencao:</strong> Esta acao nao pode ser desfeita. A operacao ficara registrada no log de auditoria com seu usuario.</p>
          </div>
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-bold" data-testid="sa-delete-cancel">
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-bold shadow-md flex items-center gap-2"
            data-testid="sa-delete-confirm"
          >
            <Trash2 size={14} />
            {busy ? 'Excluindo...' : 'Excluir Compra'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchasesPage;

